import { z } from 'zod';

import type { IntakeFormDefinition, IntakeFormField, IntakeFormValues } from './types';
import {
  DEFAULT_MAX_LENGTH,
  DEFAULT_MAX_NUMBER,
  DEFAULT_MIN_LENGTH,
  DEFAULT_MIN_NUMBER,
  IntakeFormFileValueSchema,
  IsoDateStringSchema,
} from './types';

/** Result of validating a submitted values map against a form definition. */
export interface IntakeFormValidationResult {
  /** Per-field error messages for values that were provided but malformed. */
  fieldErrors: Record<string, string> | null;
  /** True when every required field has a present, valid value. */
  isComplete: boolean;
  /** Names of required fields that are missing, empty, or invalid. */
  missingRequiredFields: string[];
  /** Provided values, with unknown keys dropped and invalid values removed. */
  sanitizedValues: IntakeFormValues;
}

/**
 * Build a zod object schema that validates a values map against a specific
 * form definition. Each field `name` maps to a per-type schema; required
 * fields are non-optional. Unknown keys are stripped (default zod behavior).
 */
export function buildIntakeFormValuesSchema(formDefinition: IntakeFormDefinition) {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const field of formDefinition.fields) {
    const base = buildFieldValueSchema(field);
    shape[field.name] = field.required ? base : base.optional();
  }
  return z.object(shape);
}

/**
 * Single validation entry point used by the server (launch, value updates, and
 * AI tooling). Separates "required field missing" from "provided value
 * malformed" so callers can decide whether to block.
 */
export function validateIntakeFormValues(
  formDefinition: IntakeFormDefinition,
  values: IntakeFormValues
): IntakeFormValidationResult {
  const missingRequiredFields = getMissingRequiredFields(formDefinition, values);
  const fieldErrors = collectProvidedFieldErrors(formDefinition, values);
  return {
    fieldErrors,
    isComplete: missingRequiredFields.length === 0,
    missingRequiredFields,
    sanitizedValues: sanitizeIntakeFormValues(formDefinition, values),
  };
}

/**
 * True when every required field has a present, non-empty value that passes
 * its per-field schema. Launch readiness gating sets `completedAt` only when
 * this is true.
 */
export function getIsIntakeFormComplete(
  formDefinition: IntakeFormDefinition,
  values: IntakeFormValues
): boolean {
  return getMissingRequiredFields(formDefinition, values).length === 0;
}

/** Returns the `name`s of required fields that are missing, empty, or invalid. */
export function getMissingRequiredFields(
  formDefinition: IntakeFormDefinition,
  values: IntakeFormValues
): string[] {
  const missing: string[] = [];
  for (const field of formDefinition.fields) {
    if (!field.required) continue;
    const value = values[field.name];
    if (getIsEmptyValue(value)) {
      missing.push(field.name);
      continue;
    }
    // A required checkbox must be affirmatively checked (true), not merely present.
    if (field.type === 'checkbox' && value !== true) {
      missing.push(field.name);
      continue;
    }
    if (!buildFieldValueSchema(field).safeParse(value).success) missing.push(field.name);
  }
  return missing;
}

/**
 * Drop keys not in the form definition and validate each known field, keeping
 * only values that parse. Invalid values are omitted rather than throwing, so a
 * partial save never errors.
 */
export function sanitizeIntakeFormValues(
  formDefinition: IntakeFormDefinition,
  values: IntakeFormValues
): IntakeFormValues {
  const sanitized: IntakeFormValues = {};
  for (const field of formDefinition.fields) {
    const value = values[field.name];
    if (getIsEmptyValue(value)) continue;
    const result = buildFieldValueSchema(field).safeParse(value);
    if (result.success) sanitized[field.name] = result.data;
  }
  return sanitized;
}

/**
 * Return a copy of `values` with the given document ids removed from every
 * fileUpload field's array. Used when an intake-uploaded document is deleted
 * from the workflow, so the stored snapshot never references a destroyed
 * Document. `changed` is false when nothing matched (caller can skip the write).
 */
export function removeFileDocumentIdsFromValues(
  formDefinition: IntakeFormDefinition,
  values: IntakeFormValues,
  documentIdsToRemove: ReadonlySet<string>
): { changed: boolean; values: IntakeFormValues } {
  if (documentIdsToRemove.size === 0) return { changed: false, values };

  const fileFieldNames = new Set(
    formDefinition.fields.filter(field => field.type === 'fileUpload').map(field => field.name)
  );
  let changed = false;
  const next: IntakeFormValues = { ...values };
  for (const name of fileFieldNames) {
    const value = next[name];
    if (!Array.isArray(value)) continue;
    const filtered = value.filter(item => {
      const file = IntakeFormFileValueSchema.safeParse(item);
      return !(file.success && documentIdsToRemove.has(file.data.documentId));
    });
    if (filtered.length !== value.length) {
      next[name] = filtered;
      changed = true;
    }
  }
  return changed ? { changed, values: next } : { changed: false, values };
}

// --- helpers (non-exported) ------------------------------------------------

/** Collects parse errors for values that were provided but do not match. */
function collectProvidedFieldErrors(
  formDefinition: IntakeFormDefinition,
  values: IntakeFormValues
): Record<string, string> | null {
  const errors: Record<string, string> = {};
  for (const field of formDefinition.fields) {
    const value = values[field.name];
    /**
     * Empty/cleared values are treated as absent, not malformed (mirrors the
     * required path). sanitizeIntakeFormValues drops them from storage, so a
     * user clearing an optional field is a valid no-op, never a hard error.
     */
    if (getIsEmptyValue(value)) continue;
    const result = buildFieldValueSchema(field).safeParse(value);
    if (!result.success) errors[field.name] = result.error.issues[0]?.message ?? 'Invalid value.';
  }
  return Object.keys(errors).length > 0 ? errors : null;
}

export function getIsEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function buildFieldValueSchema(field: IntakeFormField): z.ZodTypeAny {
  switch (field.type) {
    case 'text': {
      if (field.validation.format === 'email') return z.email();
      let schema = z.string();
      if (field.validation.minLength !== DEFAULT_MIN_LENGTH)
        schema = schema.min(field.validation.minLength);
      if (field.validation.maxLength !== DEFAULT_MAX_LENGTH)
        schema = schema.max(field.validation.maxLength);
      return schema;
    }
    case 'textarea': {
      let schema = z.string();
      if (field.validation.minLength !== DEFAULT_MIN_LENGTH)
        schema = schema.min(field.validation.minLength);
      if (field.validation.maxLength !== DEFAULT_MAX_LENGTH)
        schema = schema.max(field.validation.maxLength);
      return schema;
    }
    case 'number': {
      let schema = z.number();
      if (field.validation.min !== DEFAULT_MIN_NUMBER) schema = schema.min(field.validation.min);
      if (field.validation.max !== DEFAULT_MAX_NUMBER) schema = schema.max(field.validation.max);
      return schema;
    }
    case 'date': {
      const min = field.validation.min;
      const max = field.validation.max;
      // Real calendar-date validation (shared with the definition schema).
      if (min === undefined && max === undefined) return IsoDateStringSchema;
      return IsoDateStringSchema.refine(
        value => (min === undefined || value >= min) && (max === undefined || value <= max),
        { message: 'Date is outside the allowed range.' }
      );
    }
    case 'select': {
      const allowed = new Set(field.options.map(option => option.value));
      return z
        .string()
        .refine(value => allowed.has(value), { message: 'Invalid option selected.' });
    }
    case 'checkbox':
      return z.boolean();
    case 'checklist': {
      const allowed = new Set(field.options.map(option => option.value));
      return z
        .array(
          z.string().refine(value => allowed.has(value), { message: 'Invalid option selected.' })
        )
        .max(field.options.length)
        .refine(values => new Set(values).size === values.length, {
          message: 'Duplicate options are not allowed.',
        });
    }
    case 'fileUpload': {
      return z.array(IntakeFormFileValueSchema).max(field.validation.maxFiles);
    }
  }
}
